import { ANSI16, ANSI256, nearestAnsi16, nearestAnsi256 } from './Palette';
import { Attr, AttrMask, Color, ColorMode, TRANSPARENT, unpackRgb } from '../core/types';

export const ESC = '\u001b';
export const CSI = `${ESC}[`;

/** Terminal graphic-rendition state. TRANSPARENT means "terminal default". */
export interface Style {
  fg: Color;
  bg: Color;
  attrs: AttrMask;
}

export const DEFAULT_STYLE: Style = { fg: TRANSPARENT, bg: TRANSPARENT, attrs: Attr.NONE };

export function styleEquals(a: Style, b: Style): boolean {
  return a.fg === b.fg && a.bg === b.bg && a.attrs === b.attrs;
}

/**
 * One SGR parameter with its sub-parameters.
 * `38;2;r;g;b` yields [38],[2],[r],[g],[b]; `38:2::r:g:b` yields one entry
 * [38,2,-1,r,g,b]. Both forms appear in the wild, so both are supported.
 */
export type SgrParam = number[];

export function parseSgrParams(raw: string): SgrParam[] {
  if (raw === '') return [[0]];
  return raw.split(';').map((part) =>
    part.split(':').map((v) => {
      if (v === '') return -1;
      const n = Number.parseInt(v, 10);
      return Number.isNaN(n) ? -1 : n;
    }),
  );
}

/** Applies a parsed SGR sequence to a style, returning the new style. */
export function applySgr(style: Style, params: SgrParam[]): Style {
  let { fg, bg, attrs } = style;
  for (let i = 0; i < params.length; i++) {
    const group = params[i];
    const code = group[0] < 0 ? 0 : group[0];

    // Colon form carries everything in one group.
    if ((code === 38 || code === 48 || code === 58) && group.length > 1) {
      const color = colorFromSubParams(group.slice(1));
      if (color !== undefined) {
        if (code === 38) fg = color;
        else if (code === 48) bg = color;
      }
      continue;
    }

    // Semicolon form: consume the following groups.
    if (code === 38 || code === 48 || code === 58) {
      const kind = params[i + 1]?.[0];
      if (kind === 5) {
        const idx = params[i + 2]?.[0] ?? 0;
        const color = ANSI256[Math.max(0, Math.min(255, idx))];
        if (code === 38) fg = color;
        else if (code === 48) bg = color;
        i += 2;
      } else if (kind === 2) {
        const r = params[i + 2]?.[0] ?? 0;
        const g = params[i + 3]?.[0] ?? 0;
        const b = params[i + 4]?.[0] ?? 0;
        const color = ((r & 0xff) << 16) | ((g & 0xff) << 8) | (b & 0xff);
        if (code === 38) fg = color;
        else if (code === 48) bg = color;
        i += 4;
      } else {
        i += 1;
      }
      continue;
    }

    switch (code) {
      case 0:
        fg = TRANSPARENT;
        bg = TRANSPARENT;
        attrs = Attr.NONE;
        break;
      case 1: attrs |= Attr.BOLD; break;
      case 2: attrs |= Attr.DIM; break;
      case 3: attrs |= Attr.ITALIC; break;
      case 4: attrs |= Attr.UNDERLINE; break;
      case 5:
      case 6: attrs |= Attr.BLINK; break;
      case 7: attrs |= Attr.INVERSE; break;
      case 8: attrs |= Attr.HIDDEN; break;
      case 9: attrs |= Attr.STRIKE; break;
      case 21:
      case 22: attrs &= ~(Attr.BOLD | Attr.DIM); break;
      case 23: attrs &= ~Attr.ITALIC; break;
      case 24: attrs &= ~Attr.UNDERLINE; break;
      case 25: attrs &= ~Attr.BLINK; break;
      case 27: attrs &= ~Attr.INVERSE; break;
      case 28: attrs &= ~Attr.HIDDEN; break;
      case 29: attrs &= ~Attr.STRIKE; break;
      case 39: fg = TRANSPARENT; break;
      case 49: bg = TRANSPARENT; break;
      default:
        if (code >= 30 && code <= 37) fg = ANSI16[code - 30];
        else if (code >= 40 && code <= 47) bg = ANSI16[code - 40];
        else if (code >= 90 && code <= 97) fg = ANSI16[code - 90 + 8];
        else if (code >= 100 && code <= 107) bg = ANSI16[code - 100 + 8];
        break;
    }
  }
  return { fg, bg, attrs };
}

function colorFromSubParams(sub: number[]): Color | undefined {
  const kind = sub[0];
  if (kind === 5) return ANSI256[Math.max(0, Math.min(255, sub[1] ?? 0))];
  if (kind === 2) {
    // The spec form is 2:colorspace:r:g:b; the colorspace slot is often empty.
    const nums = sub.slice(1).filter((n) => n >= 0);
    const [r, g, b] = nums.length >= 4 ? nums.slice(1) : nums;
    return ((r & 0xff) << 16) | ((g & 0xff) << 8) | (b & 0xff);
  }
  return undefined;
}

const ATTR_ON: Array<[number, number]> = [
  [Attr.BOLD, 1],
  [Attr.DIM, 2],
  [Attr.ITALIC, 3],
  [Attr.UNDERLINE, 4],
  [Attr.BLINK, 5],
  [Attr.INVERSE, 7],
  [Attr.HIDDEN, 8],
  [Attr.STRIKE, 9],
];

const ATTR_OFF: Array<[number, number]> = [
  [Attr.BOLD, 22],
  [Attr.DIM, 22],
  [Attr.ITALIC, 23],
  [Attr.UNDERLINE, 24],
  [Attr.BLINK, 25],
  [Attr.INVERSE, 27],
  [Attr.HIDDEN, 28],
  [Attr.STRIKE, 29],
];

export function fgParams(color: Color, mode: ColorMode): number[] {
  if (color === TRANSPARENT) return [39];
  if (mode === 'ansi16') {
    const i = nearestAnsi16(color);
    return [i < 8 ? 30 + i : 90 + (i - 8)];
  }
  if (mode === 'ansi256') return [38, 5, nearestAnsi256(color)];
  const [r, g, b] = unpackRgb(color);
  return [38, 2, r, g, b];
}

export function bgParams(color: Color, mode: ColorMode): number[] {
  if (color === TRANSPARENT) return [49];
  if (mode === 'ansi16') {
    const i = nearestAnsi16(color);
    return [i < 8 ? 40 + i : 100 + (i - 8)];
  }
  if (mode === 'ansi256') return [48, 5, nearestAnsi256(color)];
  const [r, g, b] = unpackRgb(color);
  return [48, 2, r, g, b];
}

/**
 * Minimal SGR sequence to get from `from` to `to`.
 *
 * Turning an attribute off usually costs one code, but several attributes use
 * shared disable codes (bold/dim are both 22), so when more than one attribute
 * must be cleared it is both shorter and safer to emit a full reset and rebuild.
 */
export function sgrTransition(from: Style, to: Style, mode: ColorMode): string {
  if (styleEquals(from, to)) return '';

  const turnedOff = ATTR_OFF.filter(([bit]) => (from.attrs & bit) !== 0 && (to.attrs & bit) === 0);
  const useReset = turnedOff.length > 1 || (turnedOff.length === 1 && to.attrs === 0 && to.fg === TRANSPARENT && to.bg === TRANSPARENT);

  const params: number[] = [];
  let base: Style;
  if (useReset) {
    params.push(0);
    base = DEFAULT_STYLE;
  } else {
    base = from;
    for (const [bit, code] of turnedOff) {
      if ((base.attrs & bit) !== 0) params.push(code);
    }
  }

  for (const [bit, code] of ATTR_ON) {
    if ((to.attrs & bit) !== 0 && (base.attrs & bit) === 0) params.push(code);
  }
  if (to.fg !== base.fg) params.push(...fgParams(to.fg, mode));
  if (to.bg !== base.bg) params.push(...bgParams(to.bg, mode));

  if (params.length === 0) return '';
  return `${CSI}${params.join(';')}m`;
}

export const RESET = `${CSI}0m`;

/** Renders escapes readably for the raw-ANSI inspector. */
export function escapeForDisplay(text: string): string {
  return text
    .replace(/\u001b/g, '<ESC>')
    .replace(/\u0007/g, '<BEL>')
    .replace(/\r/g, '<CR>')
    .replace(/\n/g, '<LF>\n');
}
